import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Layout, Monitor, Users, Building2, 
  MessageCircle, Sparkles, ChevronUp, ChevronDown,
  Video, MessageSquare, Layers, Link2, ArrowLeftRight,
  ArrowUpDown, TrendingUp, Wallet, PiggyBank, Shield, Bot, Rocket,
  Droplets, BarChart3, Brain, Database, Cpu, Zap, Gift, Trophy,
  CreditCard, Image, Gamepad, Radio, AlertTriangle, Bug, Umbrella,
  DollarSign, Droplet, RotateCw, Building, Target, GitBranch, List,
  HardDrive, UserCog, Star, Crown, Gem, HelpCircle, Coffee, X, ChevronRight,
  ChevronLeft, User, Mic, Presentation, Lock, Table2, BarChart2
} from 'lucide-react';
import { venueFeatures, networkingTopics } from '../data/mock';

const iconMap = {
  Layout, Monitor, Users, Building2, 
  MessageCircle, Sparkles, Layers, Link2,
  ArrowLeftRight, ArrowUpDown, TrendingUp, Wallet, PiggyBank,
  Shield, Bot, Rocket, Droplets, BarChart3, Brain, Database,
  Cpu, Zap, Gift, Trophy, CreditCard, Image, Gamepad, Radio,
  AlertTriangle, Bug, Umbrella, DollarSign, Droplet, RotateCw,
  Building, Target, GitBranch, List, HardDrive, UserCog, MessageSquare
};

/* Hotspot color palette — extracted to remove 5-deep nested ternary at render time */
const HOTSPOT_COLORS = {
  blue:   '#22d3ee',
  orange: '#f97316',
  green:  '#84cc16',
  violet: '#8b5cf6',
  red:    '#ef4444',
  yellow: '#facc15',
};
const getHotspotColor = (name) => HOTSPOT_COLORS[name] || '#9ca3af';

/* Floor button styling — replaces 2 nested ternaries in the elevator button map */
const getFloorButtonClass = (floor, isActive) => {
  if (isActive) {
    if (floor <= 2) return 'bg-orange-500 text-white';
    if (floor === 9) return 'bg-red-500 text-white';
    return 'bg-[var(--accent-primary)] text-[var(--bg-primary)]';
  }
  if (floor <= 2) return 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30';
  if (floor === 9) return 'bg-red-500/20 text-red-400 hover:bg-red-500/30';
  return 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-white';
};
const getFloorTitle = (floor) => {
  if (floor <= 2) return `Diamond ${floor}`;
  if (floor === 9) return 'Main Stage';
  return `Floor ${floor}`;
};

// Scrolling Topics Marquee Component
const ScrollingTopics = ({ topics, direction = 'left', speed = 30 }) => {
  const doubledTopics = [...topics, ...topics];
  
  return (
    <div className="overflow-hidden relative py-3">
      <motion.div
        className="flex gap-3 whitespace-nowrap"
        animate={{
          x: direction === 'left' ? ['0%', '-50%'] : ['-50%', '0%']
        }}
        transition={{
          x: {
            repeat: Infinity,
            repeatType: 'loop',
            duration: speed,
            ease: 'linear'
          }
        }}
      >
        {doubledTopics.map((topic, index) => {
          const IconComponent = iconMap[topic.icon] || Users;
          return (
            <div
              key={`${topic.id}-${index}`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] hover:border-[var(--accent-primary)] hover:bg-[var(--accent-bg)] transition-all cursor-pointer group"
            >
              <IconComponent className="w-4 h-4 text-[var(--accent-primary)]" />
              <span className="text-sm font-medium text-[var(--text-secondary)] group-hover:text-white whitespace-nowrap">
                {topic.name}
              </span>
            </div>
          );
        })}
      </motion.div>
    </div>
  );
};

// Animated Hotspot Component - Small pulsing dot with hover tooltip
const AnimatedHotspot = ({ spot, onHover, isHovered }) => {
  const colorMap = {
    orange: { ring: 'bg-orange-500', dot: 'bg-orange-400', text: 'text-orange-300', border: 'border-orange-500/60', glow: 'shadow-orange-500/50' },
    blue: { ring: 'bg-cyan-400', dot: 'bg-cyan-300', text: 'text-cyan-300', border: 'border-cyan-400/60', glow: 'shadow-cyan-400/50' },
    black: { ring: 'bg-gray-500', dot: 'bg-gray-400', text: 'text-gray-300', border: 'border-gray-500/60', glow: 'shadow-gray-500/50' },
    green: { ring: 'bg-lime-500', dot: 'bg-lime-400', text: 'text-lime-300', border: 'border-lime-500/60', glow: 'shadow-lime-500/50' },
    violet: { ring: 'bg-violet-500', dot: 'bg-violet-400', text: 'text-violet-300', border: 'border-violet-500/60', glow: 'shadow-violet-500/50' },
    red: { ring: 'bg-red-500', dot: 'bg-red-400', text: 'text-red-300', border: 'border-red-500/60', glow: 'shadow-red-500/50' },
    yellow: { ring: 'bg-yellow-500', dot: 'bg-yellow-400', text: 'text-yellow-300', border: 'border-yellow-500/60', glow: 'shadow-yellow-500/50' },
    cyan: { ring: 'bg-cyan-500', dot: 'bg-cyan-400', text: 'text-cyan-300', border: 'border-cyan-500/60', glow: 'shadow-cyan-500/50' },
  };
  
  const colors = colorMap[spot.color] || colorMap.blue;
  const Icon = spot.icon;
  
  // Smart tooltip positioning based on hotspot location
  // If hotspot is near top (y < 25%), show tooltip below
  // If hotspot is near edges (x < 20% or x > 80%), adjust horizontal position
  const isNearTop = spot.y < 25;
  const isNearBottom = spot.y > 75;
  const isNearLeft = spot.x < 20;
  const isNearRight = spot.x > 80;
  
  const getTooltipPosition = () => {
    let classes = 'absolute z-30';
    
    // Vertical positioning
    if (isNearTop) {
      classes += ' top-full mt-3'; // Show below
    } else {
      classes += ' bottom-full mb-3'; // Show above (default)
    }
    
    // Horizontal positioning
    if (isNearLeft) {
      classes += ' left-0'; // Align to left edge
    } else if (isNearRight) {
      classes += ' right-0'; // Align to right edge
    } else {
      classes += ' left-1/2 -translate-x-1/2'; // Center (default)
    }
    
    return classes;
  };
  
  const getArrowPosition = () => {
    if (isNearTop) {
      // Arrow points up (tooltip is below)
      return (
        <div 
          className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full w-0 h-0 border-l-[8px] border-r-[8px] border-b-[8px] border-transparent" 
          style={{ borderBottomColor: 'rgba(5, 7, 10, 0.98)' }} 
        />
      );
    }
    // Arrow points down (tooltip is above) - default
    return (
      <div 
        className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full w-0 h-0 border-l-[8px] border-r-[8px] border-t-[8px] border-transparent" 
        style={{ borderTopColor: 'rgba(5, 7, 10, 0.98)' }} 
      />
    );
  };

  return (
    <motion.div
      className="absolute cursor-pointer z-10"
      style={{ left: `${spot.x}%`, top: `${spot.y}%`, transform: 'translate(-50%, -50%)' }}
      onMouseEnter={() => onHover(spot)}
      onMouseLeave={() => onHover(null)}
      whileHover={{ scale: 1.3 }}
    >
      {/* Outer pulse ring */}
      <div className={`absolute inset-0 w-8 h-8 -ml-2 -mt-2 rounded-full ${colors.ring} opacity-40 animate-ping`} />
      
      {/* Middle ring */}
      <div className={`absolute inset-0 w-6 h-6 -ml-1 -mt-1 rounded-full ${colors.ring} opacity-30`} />
      
      {/* Inner dot */}
      <div className={`relative w-4 h-4 rounded-full ${colors.dot} border-2 border-white shadow-lg ${colors.glow} shadow-md flex items-center justify-center`}>
        {isHovered && Icon && <Icon className="w-2 h-2 text-white" />}
      </div>
      
      {/* Hover tooltip - DARK background with LIGHT text, smart positioning */}
      <AnimatePresence>
        {isHovered && (
          <motion.div
            initial={{ opacity: 0, y: isNearTop ? -10 : 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: isNearTop ? -10 : 10, scale: 0.9 }}
            className={`${getTooltipPosition()} px-4 py-3 border ${colors.border} rounded-xl shadow-2xl min-w-[200px] max-w-[280px]`}
            style={{ backgroundColor: 'rgba(5, 7, 10, 0.98)' }}
          >
            {/* Category badge for Foundation/Ecosystem hotspots */}
            {spot.category && (
              <div className="text-[10px] text-orange-400 font-medium mb-1.5 uppercase tracking-wider">{spot.category}</div>
            )}
            {/* Header with icon */}
            <div className="flex items-center gap-2 mb-2">
              {Icon && <Icon className={`w-4 h-4 ${colors.text}`} />}
              <div className={`text-sm font-bold ${colors.text}`}>{spot.label}</div>
            </div>
            {/* Description - lighter text for readability */}
            <div className="text-xs text-gray-200 leading-relaxed">{spot.description}</div>
            {/* Arrow */}
            {getArrowPosition()}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Collapsible Info Panel
const FloorInfoPanel = ({ floor, isCollapsed, onToggle }) => {
  const getFloorInfo = () => {
    if (floor <= 2) {
      return {
        type: 'Diamond Sponsor Floor',
        icon: Gem,
        color: 'text-orange-400',
        bgColor: 'bg-orange-500/10',
        borderColor: 'border-orange-500/30',
        description: `Diamond Sponsor ${floor}`,
        features: [
          '8 Featured Protocol Booths',
          '7 Foundation/Ecosystem Tables',
          '2 Casual Lounge Areas',
          '1 Info Desk / Event Support'
        ]
      };
    } else if (floor <= 8) {
      return {
        type: 'Networking Floor',
        icon: Star,
        color: 'text-violet-400',
        bgColor: 'bg-violet-500/10',
        borderColor: 'border-violet-500/30',
        description: `Floor ${floor}: Gold, Silver & Open Networking`,
        features: [
          '1 Gold Sponsor (3 dedicated tables)',
          '6 Silver Sponsors (1 table each)',
          '6 General Access Areas',
          '2 Casual Lounges',
          '1 Info Desk'
        ]
      };
    } else {
      return {
        type: 'Main Stage Floor',
        icon: Monitor,
        color: 'text-red-400',
        bgColor: 'bg-red-500/10',
        borderColor: 'border-red-500/30',
        description: 'Keynotes, Panels & Open Networking',
        features: [
          'Main Stage (Keynotes & Panels)',
          '11 General Access Areas',
          '2 Media & Marketing Partner Areas',
          '3 Organizer Tables',
          '1 Event Support Desk'
        ]
      };
    }
  };

  const info = getFloorInfo();
  const Icon = info.icon;

  return (
    <div className={`absolute top-4 left-4 ${info.bgColor} backdrop-blur-sm rounded-xl border ${info.borderColor} z-20 transition-all duration-300 ${isCollapsed ? 'w-auto' : 'max-w-xs'}`}>
      <div className="flex items-center justify-between p-3 cursor-pointer" onClick={onToggle}>
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${info.color}`} />
          <span className="text-sm font-semibold text-white">Floor {floor}</span>
        </div>
        <button className="p-1 hover:bg-white/10 rounded transition-colors">
          {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <X className="w-4 h-4" />}
        </button>
      </div>
      
      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3">
              <p className={`text-xs ${info.color} font-medium mb-2`}>{info.type}</p>
              <p className="text-xs text-[var(--text-muted)] mb-2">{info.description}</p>
              <ul className="space-y-1">
                {info.features.map((f, i) => (
                  <li key={i} className="text-xs text-[var(--text-secondary)] flex items-center gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${info.color.replace('text-', 'bg-')}`} />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Floor hotspot configurations based on the reference SVGs
// Positions are calibrated from the Diamond floor.svg reference (1440x810 viewBox)
const getFloorHotspots = (floor) => {
  if (floor <= 2) {
    // Diamond sponsor floor - positions extracted from Diamond floor.svg reference
    // Blue = Featured Protocols (8), Orange = Foundation/Ecosystem (7 categories), 
    // Black = Casual Lounge (2), Green = Info Desk (1)
    
    return [
      // 8 BLUE HOTSPOTS - Featured Protocols
      // Positions from reference: top row left/right, middle sections, bottom corners
      { id: 'protocol1', x: 16.9, y: 12.5, label: 'Featured Protocol 1', color: 'blue', icon: Layers, 
        description: 'Featured protocol booth - meet the team and learn about integrations' },
      { id: 'protocol2', x: 80.8, y: 12.5, label: 'Featured Protocol 2', color: 'blue', icon: Layers, 
        description: 'Featured protocol booth - meet the team and learn about integrations' },
      { id: 'protocol3', x: 13.4, y: 34.8, label: 'Featured Protocol 3', color: 'blue', icon: Layers, 
        description: 'Featured protocol booth - meet the team and learn about integrations' },
      { id: 'protocol4', x: 84.4, y: 34.8, label: 'Featured Protocol 4', color: 'blue', icon: Layers, 
        description: 'Featured protocol booth - meet the team and learn about integrations' },
      { id: 'protocol5', x: 38.8, y: 37.9, label: 'Featured Protocol 5', color: 'blue', icon: Layers, 
        description: 'Featured protocol booth - meet the team and learn about integrations' },
      { id: 'protocol6', x: 58.6, y: 37.9, label: 'Featured Protocol 6', color: 'blue', icon: Layers, 
        description: 'Featured protocol booth - meet the team and learn about integrations' },
      { id: 'protocol7', x: 9.3, y: 64.4, label: 'Featured Protocol 7', color: 'blue', icon: Layers, 
        description: 'Featured protocol booth - meet the team and learn about integrations' },
      { id: 'protocol8', x: 88.4, y: 64.4, label: 'Featured Protocol 8', color: 'blue', icon: Layers, 
        description: 'Featured protocol booth - meet the team and learn about integrations' },
      
      // 7 ORANGE HOTSPOTS - Foundation/Ecosystem (max 8 team members total)
      // Categories: Grants, Build/Migrate, Incubation, Marketing, Developer Support, BD, General
      { id: 'foundation1', x: 29.7, y: 58.5, label: 'Grants', color: 'orange', icon: Gift, 
        description: 'Foundation grants & funding programs - learn about available opportunities',
        category: 'Foundation/Ecosystem' },
      { id: 'foundation2', x: 42.6, y: 58.5, label: 'Build/Migrate', color: 'orange', icon: Rocket, 
        description: 'Technical support for building on or migrating to the ecosystem',
        category: 'Foundation/Ecosystem' },
      { id: 'foundation3', x: 55.1, y: 58.5, label: 'Incubation', color: 'orange', icon: TrendingUp, 
        description: 'Incubation programs and accelerator opportunities for promising projects',
        category: 'Foundation/Ecosystem' },
      { id: 'foundation4', x: 67.8, y: 58.5, label: 'Marketing', color: 'orange', icon: MessageSquare, 
        description: 'Marketing partnerships and co-marketing opportunities',
        category: 'Foundation/Ecosystem' },
      { id: 'foundation5', x: 27.4, y: 78, label: 'Developer/Builder Support', color: 'orange', icon: Cpu, 
        description: 'Technical resources and developer relations support',
        category: 'Foundation/Ecosystem' },
      { id: 'foundation6', x: 70.3, y: 78, label: 'Business Development', color: 'orange', icon: Target, 
        description: 'BD partnerships and integration discussions',
        category: 'Foundation/Ecosystem' },
      { id: 'foundation7', x: 48.9, y: 90.2, label: 'General Enquiries', color: 'orange', icon: Crown, 
        description: 'General inquiries and ecosystem overview',
        category: 'Foundation/Ecosystem' },
      
      // 1 GREEN HOTSPOT - Info Desk/Event Support
      { id: 'info', x: 32.2, y: 29.3, label: 'Info Desk / Event Support', color: 'green', icon: HelpCircle, 
        description: 'Event support staff - get help with navigation, technical issues, or general questions' },
      
      // 2 BLACK HOTSPOTS - Casual Lounge Areas
      { id: 'lounge1', x: 49.0, y: 22.0, label: 'Casual Lounge', color: 'black', icon: Coffee, 
        description: 'Relaxed networking area - grab a virtual coffee and chat' },
      { id: 'lounge2', x: 23.3, y: 45.4, label: 'Casual Lounge', color: 'black', icon: Coffee, 
        description: 'Informal conversations and spontaneous networking' },
    ];
  } else if (floor <= 8) {
    // Networking floors (3-8) - positions extracted from floor 3-8.svg reference
    // Violet = Gold Sponsor (1 per floor, 3 tables), Red = Silver Sponsors, 
    // Black = Casual Lounge (2), Green = Info Desk (1), Yellow = General Access (6)
    
    return [
      // 3 VIOLET HOTSPOTS - Gold Sponsor (1 sponsor gets 3 dedicated tables)
      // All positioned at the bottom of the floor
      { id: 'gold1', x: 28.4, y: 82.1, label: 'Gold Sponsor Table 1', color: 'violet', icon: Star, 
        description: 'Gold sponsor dedicated table - premium networking spot' },
      { id: 'gold2', x: 48.9, y: 82.1, label: 'Gold Sponsor Table 2', color: 'violet', icon: Star, 
        description: 'Gold sponsor dedicated table - premium networking spot' },
      { id: 'gold3', x: 70.3, y: 82.1, label: 'Gold Sponsor Table 3', color: 'violet', icon: Star, 
        description: 'Gold sponsor dedicated table - premium networking spot' },
      
      // 6 RED HOTSPOTS - Silver Sponsors (1 table each)
      // Positioned around the edges of the floor
      { id: 'silver1', x: 16.9, y: 12.5, label: 'Silver Sponsor', color: 'red', icon: Building2, 
        description: 'Silver sponsor booth - meet the core team' },
      { id: 'silver2', x: 80.8, y: 12.5, label: 'Silver Sponsor', color: 'red', icon: Building2, 
        description: 'Silver sponsor booth - meet the core team' },
      { id: 'silver3', x: 13.4, y: 34.8, label: 'Silver Sponsor', color: 'red', icon: Building2, 
        description: 'Silver sponsor booth - meet the core team' },
      { id: 'silver4', x: 84.4, y: 34.8, label: 'Silver Sponsor', color: 'red', icon: Building2, 
        description: 'Silver sponsor booth - meet the core team' },
      { id: 'silver5', x: 9.3, y: 64.4, label: 'Silver Sponsor', color: 'red', icon: Building2, 
        description: 'Silver sponsor booth - meet the core team' },
      { id: 'silver6', x: 88.4, y: 64.4, label: 'Silver Sponsor', color: 'red', icon: Building2, 
        description: 'Silver sponsor booth - meet the core team' },
      
      // 6 YELLOW HOTSPOTS - General Access (open to all attendees)
      { id: 'general1', x: 38.8, y: 37.9, label: 'General Access', color: 'yellow', icon: Users, 
        description: 'Open networking area - all attendees welcome to connect' },
      { id: 'general2', x: 58.6, y: 37.9, label: 'General Access', color: 'yellow', icon: Users, 
        description: 'Open networking area - all attendees welcome to connect' },
      { id: 'general3', x: 29.7, y: 58.5, label: 'General Access', color: 'yellow', icon: Users, 
        description: 'Open networking area - all attendees welcome to connect' },
      { id: 'general4', x: 42.6, y: 58.5, label: 'General Access', color: 'yellow', icon: Users, 
        description: 'Open networking area - all attendees welcome to connect' },
      { id: 'general5', x: 55.1, y: 58.5, label: 'General Access', color: 'yellow', icon: Users, 
        description: 'Open networking area - all attendees welcome to connect' },
      { id: 'general6', x: 67.8, y: 58.5, label: 'General Access', color: 'yellow', icon: Users, 
        description: 'Open networking area - all attendees welcome to connect' },
      
      // 1 GREEN HOTSPOT - Info Desk/Event Support
      { id: 'info', x: 32.2, y: 29.3, label: 'Info Desk / Event Support', color: 'green', icon: HelpCircle, 
        description: 'Event support staff - get help with navigation, technical issues, or general questions' },
      
      // 2 BLACK HOTSPOTS - Casual Lounge Areas
      { id: 'lounge1', x: 49.0, y: 22.0, label: 'Casual Lounge', color: 'black', icon: Coffee, 
        description: 'Relaxed networking area - grab a virtual coffee and chat' },
      { id: 'lounge2', x: 23.3, y: 45.4, label: 'Casual Lounge', color: 'black', icon: Coffee, 
        description: 'Informal conversations and spontaneous networking' },
    ];
  } else {
    // Floor 9 - Main Stage - positions extracted from Floor 9.svg reference
    // Red = Main Stage (1), Orange = General Access (11), Yellow = Media & Marketing (2),
    // Green = Organizer (3 tables), Blue = Event Support (1)
    
    return [
      // 1 RED HOTSPOT - Main Stage
      { id: 'mainstage', x: 48.9, y: 20.4, label: 'Main Stage', color: 'red', icon: Monitor, 
        description: 'Keynote speakers and panel sessions - the heart of the event' },
      
      // 11 ORANGE HOTSPOTS - General Access (front row & networking)
      { id: 'general1', x: 16.9, y: 12.5, label: 'General Access', color: 'orange', icon: Users, 
        description: 'Open to all attendees - front row seats for speakers & panels' },
      { id: 'general2', x: 80.8, y: 12.5, label: 'General Access', color: 'orange', icon: Users, 
        description: 'Open to all attendees - front row seats for speakers & panels' },
      { id: 'general3', x: 13.4, y: 34.8, label: 'General Access', color: 'orange', icon: Users, 
        description: 'Open to all attendees - networking area' },
      { id: 'general4', x: 84.4, y: 34.8, label: 'General Access', color: 'orange', icon: Users, 
        description: 'Open to all attendees - networking area' },
      { id: 'general5', x: 38.8, y: 37.9, label: 'General Access', color: 'orange', icon: Users, 
        description: 'Open to all attendees - center seating' },
      { id: 'general6', x: 58.6, y: 37.9, label: 'General Access', color: 'orange', icon: Users, 
        description: 'Open to all attendees - center seating' },
      { id: 'general7', x: 23.3, y: 45.4, label: 'General Access', color: 'orange', icon: Users, 
        description: 'Open to all attendees - networking lounge' },
      { id: 'general8', x: 29.7, y: 58.5, label: 'General Access', color: 'orange', icon: Users, 
        description: 'Open to all attendees - networking area' },
      { id: 'general9', x: 42.6, y: 58.5, label: 'General Access', color: 'orange', icon: Users, 
        description: 'Open to all attendees - networking area' },
      { id: 'general10', x: 55.1, y: 58.5, label: 'General Access', color: 'orange', icon: Users, 
        description: 'Open to all attendees - networking area' },
      { id: 'general11', x: 67.8, y: 58.5, label: 'General Access', color: 'orange', icon: Users, 
        description: 'Open to all attendees - networking area' },
      
      // 2 YELLOW HOTSPOTS - Media & Marketing Partners
      { id: 'media1', x: 9.3, y: 64.4, label: 'Media & Marketing', color: 'yellow', icon: Video, 
        description: 'Media partners and marketing collaborators' },
      { id: 'media2', x: 88.4, y: 64.4, label: 'Media & Marketing', color: 'yellow', icon: Video, 
        description: 'Media partners and marketing collaborators' },
      
      // 3 GREEN HOTSPOTS - Organizer Tables
      { id: 'organizer1', x: 28.1, y: 82.1, label: 'Virtual Event Services', color: 'green', icon: Rocket, 
        description: 'Want to run your own virtual event? Talk to us!' },
      { id: 'organizer2', x: 48.5, y: 80.5, label: 'TrueKOL', color: 'green', icon: Crown, 
        description: 'TrueKOL - Web3 KOL database and influencer marketing' },
      { id: 'organizer3', x: 70.5, y: 82.1, label: 'Marketing & Branding', color: 'green', icon: MessageSquare, 
        description: 'Marketing & Branding Services Enquiry' },
      
      // 1 BLUE HOTSPOT - Event Support
      { id: 'support', x: 32.2, y: 29.3, label: 'Event Support', color: 'blue', icon: HelpCircle, 
        description: 'Event support staff - get help with navigation, technical issues, or questions' },
    ];
  }
};

// Virtual Floor Plan Component
const VirtualFloorPlan = () => {
  const [currentFloor, setCurrentFloor] = useState(1);
  const [hoveredSpot, setHoveredSpot] = useState(null);
  const [infoPanelCollapsed, setInfoPanelCollapsed] = useState(true); // Start collapsed
  const [attendeeCount, setAttendeeCount] = useState(Math.floor(Math.random() * 101 + 550)); // 550-650
  
  // Update attendee count every minute
  React.useEffect(() => {
    const interval = setInterval(() => {
      setAttendeeCount(Math.floor(Math.random() * 101 + 550)); // Random between 550-650
    }, 60000); // Every 60 seconds
    return () => clearInterval(interval);
  }, []);
  
  const SVG_URL = 'https://customer-assets.emergentagent.com/job_defi-summit/artifacts/lj5bqd7t_Propacts%20conference%20room%202.svg';
  
  const hotspots = getFloorHotspots(currentFloor);

  return (
    <div className="relative">
      {/* Floor plan container */}
      <div className="relative bg-gradient-to-b from-[#0a0d12] via-[#10141a] to-[#0a0d12] rounded-xl sm:rounded-2xl border border-[var(--border-subtle)] overflow-hidden">
        
        {/* SVG Background - responsive aspect ratio */}
        <div 
          className="relative w-full aspect-[4/3] sm:aspect-[16/10] md:aspect-[16/9] lg:aspect-[21/9] overflow-hidden"
          style={{
            backgroundImage: `url("${SVG_URL}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          {/* Dark overlay for legibility - stronger at top and bottom */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
          
          {/* Animated Hotspots - hide on very small screens */}
          <div className="hidden sm:block">
            {hotspots.map((spot) => (
              <AnimatedHotspot
                key={spot.id}
                spot={spot}
                onHover={setHoveredSpot}
                isHovered={hoveredSpot?.id === spot.id}
              />
            ))}
          </div>
          
          {/* Mobile simplified hotspots - larger tap targets */}
          <div className="sm:hidden">
            {hotspots.slice(0, 8).map((spot) => (
              <div
                key={spot.id}
                className="absolute w-4 h-4 rounded-full animate-pulse"
                style={{ 
                  left: `${spot.x}%`, 
                  top: `${spot.y}%`, 
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: getHotspotColor(spot.color)
                }}
              />
            ))}
          </div>
          
          {/* Collapsible Floor Info Panel */}
          <FloorInfoPanel 
            floor={currentFloor} 
            isCollapsed={infoPanelCollapsed}
            onToggle={() => setInfoPanelCollapsed(!infoPanelCollapsed)}
          />
          
          {/* Live indicator */}
          <div className="absolute top-2 sm:top-4 right-12 sm:right-4 flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-red-500/20 backdrop-blur-sm rounded-full border border-red-500/30">
            <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-[10px] sm:text-xs font-medium text-red-400">Live</span>
          </div>
          
          {/* Attendee count - same across all floors */}
          <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4 flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-[var(--bg-primary)]/90 backdrop-blur-sm rounded-full border border-[var(--border-subtle)]">
            <Users className="w-3 h-3 sm:w-4 sm:h-4 text-[var(--accent-primary)]" />
            <span className="text-[10px] sm:text-xs font-medium text-white">
              {attendeeCount} online
            </span>
          </div>
          
          {/* Book as Sponsor button - responsive */}
          <a 
            href="https://luma.com/6kdsrxks"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="experience-book-sponsor"
            className="absolute bottom-2 sm:bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] text-[var(--bg-primary)] font-semibold text-[10px] sm:text-sm rounded-full transition-all hover:scale-105 shadow-lg"
          >
            <Crown className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden xs:inline">Book as Sponsor</span>
            <span className="xs:hidden">Sponsor</span>
          </a>
        </div>
        
        {/* Floor Navigation - Vertical elevator style - responsive */}
        <div className="absolute right-1 sm:right-4 top-1/2 -translate-y-1/2 flex flex-col items-center gap-0.5 sm:gap-1 p-1 sm:p-2 bg-[var(--bg-secondary)]/90 backdrop-blur-sm rounded-lg sm:rounded-xl border border-[var(--border-subtle)]">
          <button 
            onClick={() => setCurrentFloor(Math.min(9, currentFloor + 1))}
            className="p-0.5 sm:p-1 rounded hover:bg-[var(--bg-tertiary)] transition-colors disabled:opacity-30"
            disabled={currentFloor === 9}
          >
            <ChevronUp className="w-3 h-3 sm:w-4 sm:h-4" />
          </button>
          
          <div className="flex flex-col gap-0.5 sm:gap-1 my-0.5 sm:my-1">
            {[9, 8, 7, 6, 5, 4, 3, 2, 1].map((floor) => (
              <button
                key={floor}
                onClick={() => setCurrentFloor(floor)}
                className={`w-5 h-4 sm:w-8 sm:h-6 rounded text-[9px] sm:text-xs font-medium transition-all ${getFloorButtonClass(floor, currentFloor === floor)}`}
                title={getFloorTitle(floor)}
              >
                {floor}
              </button>
            ))}
          </div>
          
          <button 
            onClick={() => setCurrentFloor(Math.max(1, currentFloor - 1))}
            className="p-0.5 sm:p-1 rounded hover:bg-[var(--bg-tertiary)] transition-colors disabled:opacity-30"
            disabled={currentFloor === 1}
          >
            <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4" />
          </button>
        </div>
      </div>
      
      {/* Legend - responsive */}
      <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4 mt-4 sm:mt-6 px-2 sm:px-4">
        {currentFloor <= 2 ? (
          <>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-cyan-400 animate-pulse" />
              <span className="text-[10px] sm:text-xs text-[var(--text-muted)]">Featured Protocol</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-orange-500 animate-pulse" />
              <span className="text-[10px] sm:text-xs text-[var(--text-muted)]">Foundation</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-gray-400 animate-pulse" />
              <span className="text-[10px] sm:text-xs text-[var(--text-muted)]">Lounge</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-lime-400 animate-pulse" />
              <span className="text-[10px] sm:text-xs text-[var(--text-muted)]">Info</span>
            </div>
          </>
        ) : currentFloor <= 8 ? (
          <>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-violet-500 animate-pulse" />
              <span className="text-[10px] sm:text-xs text-[var(--text-muted)]">Gold</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-red-500 animate-pulse" />
              <span className="text-[10px] sm:text-xs text-[var(--text-muted)]">Silver</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-yellow-400 animate-pulse" />
              <span className="text-[10px] sm:text-xs text-[var(--text-muted)]">General</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-gray-400 animate-pulse" />
              <span className="text-[10px] sm:text-xs text-[var(--text-muted)]">Lounge</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-lime-400 animate-pulse" />
              <span className="text-[10px] sm:text-xs text-[var(--text-muted)]">Info</span>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-red-500 animate-pulse" />
              <span className="text-[10px] sm:text-xs text-[var(--text-muted)]">Stage</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-orange-400 animate-pulse" />
              <span className="text-[10px] sm:text-xs text-[var(--text-muted)]">General</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-yellow-400 animate-pulse" />
              <span className="text-[10px] sm:text-xs text-[var(--text-muted)]">Media</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-lime-400 animate-pulse" />
              <span className="text-[10px] sm:text-xs text-[var(--text-muted)]">Organizer</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-cyan-400 animate-pulse" />
              <span className="text-[10px] sm:text-xs text-[var(--text-muted)]">Support</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const ExperienceSection = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  // Split topics into two rows for scrolling
  const topicsRow1 = networkingTopics.filter((_, i) => i < networkingTopics.length / 2);
  const topicsRow2 = networkingTopics.filter((_, i) => i >= networkingTopics.length / 2);

  return (
    <section ref={ref} className="section-padding bg-[var(--bg-secondary)]" data-testid="experience-section">
      <div className="container mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="text-center max-w-3xl mx-auto mb-8"
        >
          <span className="text-sm text-[var(--accent-primary)] uppercase tracking-widest font-medium">The Experience</span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mt-4 mb-4 md:mb-6">
            9 Floors of Immersive
            <span className="text-[var(--accent-primary)]"> Virtual Networking</span>
          </h2>
          <p className="text-sm md:text-base text-[var(--text-secondary)]">
            Navigate a premium virtual venue. Hover over hotspots to explore sponsor areas, networking tables, and open spaces.
          </p>
        </motion.div>
        
        {/* Scrolling Topics Marquee */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.1 }}
          className="mb-6 md:mb-8"
        >
          <div className="text-center mb-3 md:mb-4">
            <span className="text-[10px] sm:text-xs uppercase tracking-wider text-[var(--text-muted)]">35+ DeFi Categories</span>
          </div>
          <div className="relative">
            {/* Gradient masks */}
            <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-[var(--bg-secondary)] to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-[var(--bg-secondary)] to-transparent z-10 pointer-events-none" />
            <ScrollingTopics topics={topicsRow1} direction="left" speed={40} />
            <ScrollingTopics topics={topicsRow2} direction="right" speed={45} />
          </div>
        </motion.div>
        
        {/* Interactive Floor Plan */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2 }}
          className="mb-16"
        >
          <VirtualFloorPlan />
        </motion.div>
        
        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {venueFeatures.map((feature, index) => {
            const Icon = iconMap[feature.icon] || Layout;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.3 + index * 0.05 }}
                className="p-6 rounded-2xl bg-[var(--bg-primary)] border border-[var(--border-subtle)] hover:border-[var(--accent-primary)] transition-all group cursor-default"
              >
                <div className="w-12 h-12 rounded-xl bg-[var(--accent-bg)] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Icon className="w-6 h-6 text-[var(--accent-primary)]" />
                </div>
                <h3 className="font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-[var(--text-muted)]">{feature.description}</p>
              </motion.div>
            );
          })}
        </div>
        
        {/* Platform Features Carousel */}
        <PlatformFeaturesCarousel inView={inView} />
      </div>
    </section>
  );
};

// Platform Features Carousel Component
const PlatformFeaturesCarousel = ({ inView }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  
  const features = [
    {
      id: 'miro',
      title: 'Miro Board Integration',
      label: 'Collaboration',
      icon: Layers,
      image: 'https://customer-assets.emergentagent.com/job_6f758192-ab9f-49b2-b18c-e8fbed640b66/artifacts/ipv2pr3u_miro%20integration.gif',
      description: 'Collaborate in real-time with integrated Miro boards. Brainstorm ideas, map integrations, and visualize partnerships within the venue.',
      bullets: [
        'Real-time collaborative whiteboarding',
        'Visualize integration architectures together',
        'Export boards for post-event follow-ups'
      ]
    },
    {
      id: 'avatars',
      title: 'Customizable Avatars & Profiles',
      label: 'Personalization',
      icon: User,
      image: 'https://customer-assets.emergentagent.com/job_6f758192-ab9f-49b2-b18c-e8fbed640b66/artifacts/13e4o76y_Customizable%20User%20Profile.svg',
      description: 'Make meaningful connections with personalized avatars, profiles, and locators. Stand out and find the right people effortlessly.',
      bullets: [
        'Personalize with photos, GIFs, and status',
        'Virtual business cards for quick intros',
        'Find guests easily with user locator'
      ]
    },
    {
      id: 'video',
      title: '1080p Video & Audio Quality',
      label: 'HD Experience',
      icon: Video,
      image: 'https://customer-assets.emergentagent.com/job_6f758192-ab9f-49b2-b18c-e8fbed640b66/artifacts/qsghybas_High%20quality%20video%20and%20audio.png',
      description: 'Crystal-clear HD video and audio for seamless conversations. Every interaction feels as engaging as in-person.',
      bullets: [
        'Crisp 1080p video for lifelike experience',
        'Active speaker detection',
        'Screen share and flexible layouts'
      ]
    },
    {
      id: 'mainstage',
      title: 'Mainstage: Speakers & Presentations',
      label: 'Presentations',
      icon: Presentation,
      image: 'https://customer-assets.emergentagent.com/job_6f758192-ab9f-49b2-b18c-e8fbed640b66/artifacts/a2bfcfc5_presentation%20mode.gif',
      description: 'Spotlight your presentation for all attendees. Perfect for keynotes, panels, and sponsor presentations.',
      bullets: [
        'Broadcast live to your entire audience',
        'Interactive polls, quizzes, and Q&A',
        'Switch from networking to presentations instantly'
      ]
    },
    {
      id: 'chat',
      title: 'Live Chat: Private, Table & Event',
      label: 'Communication',
      icon: MessageSquare,
      image: 'https://customer-assets.emergentagent.com/job_6f758192-ab9f-49b2-b18c-e8fbed640b66/artifacts/det5dccm_chatbox.png',
      description: 'Multi-level chat keeps conversations flowing. Connect your way with private, table, or event-wide messaging.',
      bullets: [
        'Share texts, files, or GIFs seamlessly',
        'Private, table, or general chat options',
        'Live reactions: 👏 🎉 👍 🤔 ❤️ 😂'
      ]
    },
    {
      id: 'locked',
      title: 'Locked Tables for Privacy',
      label: 'Privacy',
      icon: Lock,
      image: 'https://customer-assets.emergentagent.com/job_6f758192-ab9f-49b2-b18c-e8fbed640b66/artifacts/dt9od7x7_Table%20Lock%20Feature.svg',
      description: 'Host private discussions without interruptions. Lock tables for focused sessions, workshops, or confidential conversations.',
      bullets: [
        'Prevent others from joining mid-conversation',
        'Perfect for focused sessions or workshops',
        'Attendees can lock tables themselves'
      ]
    },
    {
      id: 'flexi',
      title: 'Flexi-Tables',
      label: 'Flexibility',
      icon: Table2,
      image: 'https://customer-assets.emergentagent.com/job_6f758192-ab9f-49b2-b18c-e8fbed640b66/artifacts/i2vs5x42_Table%20overflow.svg',
      description: 'Dynamic, flexible table options for every conversation type. From 1-on-1 chats to group discussions.',
      bullets: [
        'Mix and match table sizes per floor',
        'Table overflow boosts capacity by 50%',
        'Shareable table links guide guests directly'
      ]
    },
    {
      id: 'analytics',
      title: 'Post-Event Analytics',
      label: 'ROI',
      icon: BarChart2,
      image: 'https://customer-assets.emergentagent.com/job_6f758192-ab9f-49b2-b18c-e8fbed640b66/artifacts/k110d1ev_Analytics.png',
      description: 'Maximize sponsor ROI with trackable leads and precise, actionable data. Prove your value with detailed reports.',
      bullets: [
        'Banner clicks and CTA interaction reports',
        'Compiled hot lead lists with contacts',
        'Personalized sponsor reports post-event'
      ]
    }
  ];
  
  // Auto-rotate every 20 seconds
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % features.length);
    }, 20000);
    
    return () => clearInterval(interval);
  }, [isPaused, features.length]);
  
  const activeFeature = features[activeIndex];
  const Icon = activeFeature.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: 0.4 }}
      className="mt-12 md:mt-16"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Section Header */}
      <div className="text-center mb-6">
        <span className="text-xs text-[var(--accent-primary)] uppercase tracking-widest font-medium">Platform Features</span>
        <h3 className="text-xl md:text-2xl font-bold mt-2">
          Built for <span className="text-[var(--accent-primary)]">Seamless Networking</span>
        </h3>
      </div>
      
      {/* Feature Tabs */}
      <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2 mb-4 sm:mb-6 px-2">
        {features.map((feature, index) => {
          const TabIcon = feature.icon;
          return (
            <button
              key={feature.id}
              onClick={() => setActiveIndex(index)}
              className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-medium transition-all ${
                activeIndex === index
                  ? 'bg-[var(--accent-primary)] text-[var(--bg-primary)]'
                  : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-white border border-[var(--border-subtle)]'
              }`}
            >
              <TabIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              <span className="hidden md:inline">{feature.label}</span>
            </button>
          );
        })}
      </div>
      
      {/* Feature Content */}
      <div className="relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIndex}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="grid lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8 items-center p-4 sm:p-6 md:p-8 rounded-2xl bg-[var(--bg-primary)] border border-[var(--border-subtle)]"
          >
            {/* Left - Image */}
            <div className="relative rounded-xl overflow-hidden border border-[var(--border-subtle)] bg-[var(--bg-tertiary)]">
              <img 
                src={activeFeature.image}
                alt={activeFeature.title}
                loading="lazy"
                className="w-full aspect-video object-contain"
              />
              <div className="absolute top-2 sm:top-3 left-2 sm:left-3 px-2 py-0.5 sm:px-2.5 sm:py-1 bg-[var(--bg-primary)]/80 backdrop-blur-sm rounded-full border border-[var(--accent-primary)]/30">
                <span className="text-[9px] sm:text-[10px] font-medium text-[var(--accent-primary)]">{activeFeature.label}</span>
              </div>
            </div>
            
            {/* Right - Content */}
            <div>
              <div className="flex items-center gap-2 mb-2 sm:mb-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-[var(--accent-bg)] flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-[var(--accent-primary)]" />
                </div>
                <h4 className="text-base sm:text-lg md:text-xl font-bold">{activeFeature.title}</h4>
              </div>
              <p className="text-xs sm:text-sm text-[var(--text-secondary)] mb-3 sm:mb-4">
                {activeFeature.description}
              </p>
              <ul className="space-y-1.5 sm:space-y-2">
                {activeFeature.bullets.map((bullet, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs sm:text-sm text-[var(--text-muted)]">
                    <div className="w-1.5 h-1.5 rounded-full bg-[var(--accent-primary)] mt-1.5 flex-shrink-0" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </AnimatePresence>
        
        {/* Navigation Arrows */}
        <button 
          onClick={() => setActiveIndex((prev) => (prev - 1 + features.length) % features.length)}
          className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-[var(--bg-secondary)]/80 backdrop-blur-sm border border-[var(--border-subtle)] flex items-center justify-center hover:border-[var(--accent-primary)] transition-colors hidden md:flex"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button 
          onClick={() => setActiveIndex((prev) => (prev + 1) % features.length)}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-[var(--bg-secondary)]/80 backdrop-blur-sm border border-[var(--border-subtle)] flex items-center justify-center hover:border-[var(--accent-primary)] transition-colors hidden md:flex"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      
      {/* Progress Indicators */}
      <div className="flex justify-center gap-1.5 mt-4">
        {features.map((_, index) => (
          <button
            key={index}
            onClick={() => setActiveIndex(index)}
            className={`h-1 rounded-full transition-all ${
              activeIndex === index
                ? 'w-6 bg-[var(--accent-primary)]'
                : 'w-1.5 bg-[var(--border-subtle)] hover:bg-[var(--text-muted)]'
            }`}
          />
        ))}
      </div>
    </motion.div>
  );
};

export default ExperienceSection;
